import { useMedia } from "react-use";

/**
 * Detects whether the device has any imprecise input (finger, stylus), meaning
 * hover cannot be relied on to drive an interaction.
 *
 * Use this for interaction decisions (hover vs click triggers), NOT for layout
 * decisions — layout should keep using `useIsMobileScreen` / `useBreakpoint`,
 * which measure viewport width instead.
 *
 * Uses `any-pointer` rather than `pointer` so hybrid devices are covered: a
 * touchscreen laptop reports `pointer: fine` because its primary input is the
 * trackpad, yet tapping the screen still leaves the emulated hover stuck on the
 * tapped element — which is what breaks hover-triggered popups.
 *
 * @returns true when the device has a coarse pointer
 *
 * @example
 * ```tsx
 * const hasCoarsePointer = useHasCoarsePointer();
 * return <Menu triggerSubMenuAction={hasCoarsePointer ? "click" : "hover"} />;
 * ```
 */
export const useHasCoarsePointer = (): boolean =>
  useMedia("(any-pointer: coarse)", false);
